/**
 * Business catalog, logistics settings, and console report contracts.
 */
import { GHALISHOR_API_V1_PREFIX } from './identity-access';
import type { IsoDateTimeString, UuidString } from './identity-access';
import type { CarpetType, ServiceCatalogKey } from './customer-requests';
import type { SystemRoleKey } from './permissions';

export const PRICE_UNITS = Object.freeze(['per_sqm', 'per_item', 'flat', 'per_floor'] as const);
export type PriceUnit = (typeof PRICE_UNITS)[number];

export const BUSINESS_CATALOG_ROUTES = Object.freeze({
  catalog: `${GHALISHOR_API_V1_PREFIX}/business/catalog`,
  logistics: `${GHALISHOR_API_V1_PREFIX}/business/logistics`,
  reportsSummary: `${GHALISHOR_API_V1_PREFIX}/business/reports/summary`,
  team: `${GHALISHOR_API_V1_PREFIX}/business/team`,
  teamInvite: `${GHALISHOR_API_V1_PREFIX}/business/team/members`,
  teamUpdateMember: (membershipId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/team/members/${encodeURIComponent(membershipId)}`,
  teamResetPassword: (membershipId: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/team/members/${encodeURIComponent(membershipId)}/reset-password`,
} as const);

export interface BusinessServiceOffering {
  readonly serviceKey: ServiceCatalogKey;
  readonly carpetType: CarpetType;
  readonly unit: PriceUnit;
  readonly unitPrice: number;
  readonly minPrice: number;
  readonly enabled: boolean;
  readonly currency: string;
}

export interface BusinessCatalogResponse {
  readonly businessId: UuidString;
  readonly offerings: readonly BusinessServiceOffering[];
  readonly platformCatalog: readonly ServiceCatalogKey[];
  readonly updatedAt: IsoDateTimeString;
}

export interface UpsertBusinessCatalogRequest {
  readonly offerings: readonly BusinessServiceOffering[];
}

export interface BusinessLogisticsSettings {
  readonly businessId: UuidString;
  readonly transportEnabled: boolean;
  readonly pickupFee: number;
  readonly deliveryFee: number;
  readonly roundTripFee: number | null;
  readonly floorCarryEnabled: boolean;
  readonly feePerFloorNoElevator: number;
  readonly feePerFloorWithElevator: number;
  readonly maxFloors: number;
  readonly updatedAt: IsoDateTimeString;
}

export interface UpdateBusinessLogisticsRequest {
  readonly transportEnabled?: boolean;
  readonly pickupFee?: number;
  readonly deliveryFee?: number;
  readonly roundTripFee?: number | null;
  readonly floorCarryEnabled?: boolean;
  readonly feePerFloorNoElevator?: number;
  readonly feePerFloorWithElevator?: number;
  readonly maxFloors?: number;
}

export interface BusinessReportsSummary {
  readonly businessId: UuidString;
  readonly acceptedCount: number;
  readonly rejectedCount: number;
  readonly completedCount: number;
  readonly activeJobs: number;
  readonly scheduledCount: number;
  readonly avgAcceptedEstimate: number | null;
  readonly ratingAvg: number | null;
  readonly ratingCount: number;
}

export interface BusinessTeamMember {
  readonly membershipId: UuidString;
  readonly userId: UuidString;
  readonly mobileE164: string;
  readonly displayName: string | null;
  readonly status: string;
  readonly isPrimaryOwner: boolean;
  readonly roleKeys: readonly SystemRoleKey[];
}

export interface BusinessTeamResponse {
  readonly businessId: UuidString;
  readonly members: readonly BusinessTeamMember[];
}

export interface InviteBusinessTeamMemberRequest {
  readonly mobile: string;
  readonly roleKey: Exclude<SystemRoleKey, 'owner'>;
  readonly temporaryPassword: string;
  readonly displayName?: string;
}

export interface UpdateBusinessTeamMemberRequest {
  readonly roleKey?: Exclude<SystemRoleKey, 'owner'>;
  readonly status?: 'active' | 'inactive';
}

export interface ResetBusinessTeamMemberPasswordRequest {
  readonly temporaryPassword: string;
}

export function isPriceUnit(value: unknown): value is PriceUnit {
  return typeof value === 'string' && (PRICE_UNITS as readonly string[]).includes(value);
}
